import { useConnectionAnalytics } from 'Modules/softphone/hooks/useConnectionAnalytics';
import { useOrphanedFocusRescue } from 'Modules/softphone/hooks/useOrphanedFocusRescue';
import React, { useEffect, useId, useRef } from 'react';
import { Icon } from '~/components/shared/Icon';
import { cn } from '~/lib/utils';

/**
 * What covers the call while the transport is down.
 *
 * @remarks
 * Takes focus when it appears: the call information behind it is switched to
 * `visibility: hidden`, so a screen reader's cursor would otherwise relocate to
 * the action buttons below. {@link useOrphanedFocusRescue} only acts on focus
 * that is already nowhere.
 *
 * Named by its own contents, so landing here reads the whole message. No
 * `aria-live` — the shared `LiveAnnouncer` already speaks this event.
 */
export const SoftphoneConnectionLossOverlay = (): React.ReactElement => {
  const { trackLostConnectionBannerShown } = useConnectionAnalytics();

  const overlayRef = useRef<HTMLElement | null>(null);
  useOrphanedFocusRescue(overlayRef);

  const headingId = useId();
  const detailId = useId();

  useEffect(() => {
    trackLostConnectionBannerShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      ref={overlayRef}
      // Focus target without a tab stop.
      tabIndex={-1}
      aria-labelledby={`${headingId} ${detailId}`}
      className={cn(
        'absolute h-52 pt-[33%] z-10 w-full flex flex-col items-center gap-[12px] pointer-events-none bg-background focus:outline-none'
      )}
    >
      <div className="flex justify-center items-center gap-[8px]">
        <Icon icon="warning-orange" aria-hidden="true" size="medium" />
        <h3
          id={headingId}
          className="text-[16px] leading-[19px] font-bold text-foreground"
        >
          You have lost connection
        </h3>
      </div>
      <p id={detailId} className="text-body font-normal text-foreground m-0">
        Trying to reconnect…
      </p>
    </section>
  );
};
